import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Card, EmptyState, IconButton, Menu, PageHeader, Pill, useConfirm, useToast } from '../ui';
import {
  BoxIcon, DownloadIcon, EditIcon, LinkIcon, MoreIcon, PlusIcon, RefreshIcon, TrashIcon, UploadIcon,
} from '../ui/icons';
import api from '../config/api';
import { sectionCrumb } from '../settings/SectionLayout';
import { hasSources, presetInfo, presetUses, uiLang, useKinds, usedFields } from '../kinds';
import { errorMessage } from '../errors';
import { showImportResult } from './importResult';

const PREVIEW_FIELDS = 4;

/**
 * /settings/item-types — the caller's item types (and those of collections
 * shared with them) as cards: the model a type was made from, its fields,
 * and what can be done with it (edit, TMDB integration, conversion to a
 * model, duplicate, delete). Export and import of every type.
 */
function ItemTypeList({ itemTypes, onDelete, onCreateNew, onRefresh }) {
  const { t, i18n } = useTranslation();
  const lang = uiLang(i18n);
  const kinds = useKinds();
  const toast = useToast();
  const confirm = useConfirm();
  const fileInputRef = useRef(null);

  const handleExport = async () => {
    try {
      const response = await api.get('/item-types/export', { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `item_types_export_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting item types:', error);
      toast.error(t('itemTypes.exportError'));
    }
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    try {
      const importData = JSON.parse(await file.text());
      // Accept both old and new format for backward compatibility
      if (!['item_types', 'inventory_types'].includes(importData.type)) {
        toast.error(t('itemTypes.invalidFile'));
        return;
      }
      const response = await api.post('/item-types/import', importData);
      if (response.data.success) {
        showImportResult(toast, t, response.data);
        if (onRefresh) onRefresh();
      } else {
        toast.error(t('settings.importFailed', { error: response.data.error || t('common.unknownError') }));
      }
    } catch (error) {
      console.error('Error importing item types:', error);
      toast.error(t('itemTypes.importError', { error: errorMessage(t, error) }));
    } finally {
      event.target.value = '';
    }
  };

  const handleDuplicate = async (itemType) => {
    if (!(await confirm({
      title: t('itemTypes.confirmDuplicateTitle'),
      message: t('itemTypes.confirmDuplicate', { name: itemType.name }),
      confirmLabel: t('itemTypes.duplicate'),
    }))) return;
    try {
      const response = await api.post(`/item-types/${itemType.id}/duplicate`);
      toast.success(t('itemTypes.duplicated', { name: response.data.name }));
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Error duplicating item type:', error);
      toast.error(t('itemTypes.duplicateError', { error: errorMessage(t, error) }));
    }
  };

  const handleDelete = async (itemType) => {
    if (await confirm({
      title: t('itemTypes.confirmDeleteTitle'),
      message: t('itemTypes.confirmDelete', { name: itemType.name }),
      confirmLabel: t('itemTypes.deleteType'),
      danger: true,
    })) onDelete(itemType.id);
  };

  const menuItems = (itemType) => [
    itemType.is_mine && !itemType.preset && !itemType.conversion_running && {
      id: 'convert', label: t('itemTypes.convert'), icon: RefreshIcon, to: `/settings/item-types/${itemType.id}/convert`,
    },
    { id: 'duplicate', label: t('itemTypes.duplicate'), icon: PlusIcon, onSelect: () => handleDuplicate(itemType) },
    itemType.is_mine && 'separator',
    itemType.is_mine && { id: 'delete', label: t('common.delete'), icon: TrashIcon, danger: true, onSelect: () => handleDelete(itemType) },
  ].filter(Boolean);

  return (
    <div>
      <PageHeader
        breadcrumb={[sectionCrumb(t, 'settings'), { label: t('itemTypes.title') }]}
        title={t('itemTypes.title')}
        actions={(
          <>
            <Button variant="secondary" icon={DownloadIcon} onClick={handleExport}>{t('settings.export')}</Button>
            <Button variant="secondary" icon={UploadIcon} onClick={() => fileInputRef.current.click()}>{t('settings.import')}</Button>
            <Button icon={PlusIcon} onClick={onCreateNew}>{t('itemTypes.create')}</Button>
          </>
        )}
      />

      <input type="file" ref={fileInputRef} onChange={handleFileSelect} accept=".json" hidden />

      {itemTypes.length === 0 ? (
        <EmptyState icon={BoxIcon} title={t('itemTypes.emptyTitle')}
                    action={<Button icon={PlusIcon} onClick={onCreateNew}>{t('itemTypes.create')}</Button>}>
          {t('itemTypes.empty')}
        </EmptyState>
      ) : (
        <ul className="type-grid">
          {itemTypes.map(itemType => {
            const preset = itemType.preset && presetInfo(kinds, itemType.preset);
            const used = usedFields(itemType.fields);
            const unused = (itemType.fields || []).length - used.length;
            const detailOnly = used.filter(field => field.in_view === false).length;
            const titleId = `item-type-${itemType.id}`;
            return (
              <li key={itemType.id}>
                <Card as="article" className="type-card" aria-labelledby={titleId}>
                  <div className="type-card__head">
                    <h2 id={titleId} className="type-card__title">{itemType.name}</h2>
                    <Menu
                      label={t('itemTypes.actionsFor', { name: itemType.name })}
                      align="end"
                      items={menuItems(itemType)}
                      trigger={(props) => (
                        <IconButton {...props} icon={MoreIcon} variant="ghost" label={t('itemTypes.actionsFor', { name: itemType.name })} />
                      )}
                    />
                  </div>
                  {(preset || !itemType.is_mine) && (
                    <div className="type-card__badges">
                      {preset && <Pill tone="accent" title={t('itemTypes.kindBadgeTitle')}>{preset.preset.labels[lang]}</Pill>}
                      {!itemType.is_mine && <Pill>{t('settings.shared')}</Pill>}
                    </div>
                  )}
                  <p className="type-card__description">{itemType.description || t('itemTypes.noDescription')}</p>
                  <p className="type-card__meta">
                    {t('collections.fieldCount', { count: used.length })}
                    {unused > 0 && ` ${t('itemTypes.unusedCount', { count: unused })}`}
                    {detailOnly > 0 && ` · ${t('itemTypes.detailOnlyCount', { count: detailOnly })}`}
                  </p>
                  {used.length > 0 && (
                    <ul className="tag-list type-card__fields" aria-label={t('itemTypes.fieldsOf', { name: itemType.name })}>
                      {used.slice(0, PREVIEW_FIELDS).map(field => (
                        <li key={field.key || field.name} className="ui-chip ui-chip--static">
                          {field.name}
                          <span className="type-card__field-type">{t(`fieldType.${field.type}`, { defaultValue: field.type })}</span>
                        </li>
                      ))}
                      {used.length > PREVIEW_FIELDS && (
                        <li className="tag-list__more">{t('itemTypes.moreFields', { count: used.length - PREVIEW_FIELDS })}</li>
                      )}
                    </ul>
                  )}
                  {itemType.is_mine && itemType.conversion_running && (
                    <Alert tone="warning" className="type-card__alert">{t('itemTypes.finishConversionTitle')}</Alert>
                  )}
                  <div className="type-card__actions">
                    {itemType.is_mine && itemType.conversion_running && (
                      <Button as={Link} to={`/settings/item-types/${itemType.id}/convert`} size="sm" icon={RefreshIcon}>
                        {t('itemTypes.finishConversion')}
                      </Button>
                    )}
                    {itemType.is_mine && !itemType.conversion_running && (
                      <Button as={Link} to={`/settings/item-types/${itemType.id}`} size="sm" variant="secondary" icon={EditIcon}
                              aria-label={t('itemTypes.editNamed', { name: itemType.name })}>
                        {t('common.edit')}
                      </Button>
                    )}
                    {hasSources(itemType.preset, kinds) && (
                      <Button as={Link} to={`/settings/item-types/${itemType.id}/integration`} size="sm" variant="ghost" icon={LinkIcon}
                              title={t(presetUses(itemType.preset, 'tmdb', kinds) ? 'itemTypes.integrationTitle' : 'itemTypes.sourcesTitle')}>
                        {t(presetUses(itemType.preset, 'tmdb', kinds) ? 'itemTypes.integration' : 'itemTypes.sources')}
                      </Button>
                    )}
                  </div>
                </Card>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default ItemTypeList;
